import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';
import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';

export default class EdWboDrawerZoom {
    private wboDrawerTools: WboDrawerTools;
    private ZOOM_FACTOR = 0.5;
    private origin = {
        scrollX: 0,
        scrollY: 0,
        x: 0.0,
        y: 0.0,
        clientY: 0,
        scale: 1.0,
        distance: 0.0
    };
    private moved = false;
    private pressed = false;
    private svg: SVGSVGElement;
    private board: HTMLDivElement;
    private pageContainer: HTMLDivElement;
    private animation: number = null;

    private zoomTool: BoardTool = {
        name: 'Zoom',
        shortcut: 'z',
        listeners: {
            press: (x: number, y: number, evt, isTouchEvent: boolean) => this.press(x, y, evt, isTouchEvent),
            move: (x: number, y: number, evt, isTouchEvent: boolean) => this.move(x, y, evt, isTouchEvent),
            release: (x: number, y: number, evt, isTouchEvent: boolean) => this.release(x, y, evt, isTouchEvent),
        },
        onstart: () => this.onstart(),
        onquit: () => this.onquit(),
        mouseCursor: 'zoom-in',
        icon: 'tools/zoom/search-solid.svg',
        helpText: 'click_to_zoom',
        showMarker: true
    }
    private keyDown: EventListener;
    private keyUp: EventListener;

    constructor(tools: WboDrawerTools) {
        this.wboDrawerTools = tools;
        this.svg = tools.svg;
        this.board = tools.board;
        this.pageContainer = tools.pageContainer;
        // this.board.addEventListener('wheel', (evt: WheelEvent) => this.onwheel(evt), {passive: false});
        this.board.addEventListener('touchmove', (evt: TouchEvent) => this.ontouchmove(evt), {passive: true});
        this.board.addEventListener('touchend', () => this.touchend());
        this.board.addEventListener('touchcancel', () => this.touchend());

        this.wboDrawerTools.add(this.zoomTool);
        this.keyDown = this.key(true);
        this.keyUp = this.key(false);
    }

    private press(x: number, y: number, evt: MouseEvent, isTouchEvent: boolean): void {
        evt.preventDefault();
        this.setOrigin(x, y, evt, isTouchEvent);
        this.moved = false;
        this.pressed = true;
    }

    private move(x: number, y: number, evt: MouseEvent, isTouchEvent: boolean): void {
        if (this.pressed) {
            evt.preventDefault();
            const delta = this.getClientY(evt, isTouchEvent) - this.origin.clientY;
            const scale = this.origin.scale * (1 + delta * this.ZOOM_FACTOR / 100);
            if (Math.abs(delta) > 1) {
                this.moved = true;
            }
            this.animation = this.animate(scale);
        }
    }

    private release(x: number, y: number, evt: MouseEvent, isTouchEvent: boolean): void {
        if (this.pressed && !this.moved) {
            const delta = (evt.shiftKey === true) ? -1 : 1;
            const scale = this.wboDrawerTools.getScale() * (1 + delta * this.ZOOM_FACTOR);
            this.zoom(this.origin, scale);
        }
        this.pressed = false;
    }

    private key: Function = (down: boolean) => {
        return (evt) => {
            if (evt.key === 'Shift') {
                this.svg.style.cursor = 'zoom-' + (down ? 'out' : 'in');
            }
        }
    }


    private onstart(): void {
        window.addEventListener('keydown', this.keyDown);
        window.addEventListener('keyup', this.keyUp);
    }

    private onquit(): void {
        window.removeEventListener('keydown', this.keyDown);
        window.removeEventListener('keyup', this.keyUp);
    }

    private onwheel(evt: WheelEvent): void {
        evt.preventDefault();
        const multiplier =
            (evt.deltaMode === WheelEvent.DOM_DELTA_LINE) ? 30 :
                (evt.deltaMode === WheelEvent.DOM_DELTA_PAGE) ? 1000 :
                    1;
        const deltaX = evt.deltaX * multiplier, deltaY = evt.deltaY * multiplier;
        if (!evt.ctrlKey) {
            // zoom
            const scale = this.wboDrawerTools.getScale();
            const x = evt.pageX / scale;
            const y = evt.pageY / scale;
            this.setOrigin(x, y, evt, false);
            this.animate((1 - deltaY / 800) * this.wboDrawerTools.getScale());
        } else if (evt.altKey) {
            // make finer changes if shift is being held
            const change = evt.shiftKey ? 1 : 5;
            // change tool size
            this.wboDrawerTools.setSize(this.wboDrawerTools.getSize() - deltaY / 100 * change);
        } else if (evt.shiftKey) {
            // scroll horizontally
            window.scrollTo(this.pageContainer.scrollLeft + deltaY, this.pageContainer.scrollTop + deltaX);
        } else {
            // regular scrolling
            window.scrollTo(this.pageContainer.scrollLeft + deltaX, this.pageContainer.scrollTop + deltaY);
        }
    }


    private ontouchmove(evt: TouchEvent): void {
        // 2-finger pan to zoom
        var touches = evt.touches;
        if (touches.length === 2) {
            const x0 = touches[0].clientX, x1 = touches[1].clientX,
                y0 = touches[0].clientY, y1 = touches[1].clientY,
                dx = x0 - x1,
                dy = y0 - y1;
            const x = (touches[0].pageX + touches[1].pageX) / 2 / this.wboDrawerTools.getScale(),
                y = (touches[0].pageY + touches[1].pageY) / 2 / this.wboDrawerTools.getScale();
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (!this.pressed) {
                this.pressed = true;
                this.setOrigin(x, y, evt, true);
                this.origin.distance = distance;
            } else {
                const delta = distance - this.origin.distance;
                const scale = this.origin.scale * (1 + delta * this.ZOOM_FACTOR / 100);
                this.animate(scale);
            }
        }
    }

    private touchend(): void {
        this.pressed = false;
    }

    private setOrigin(x: number, y: number, evt, isTouchEvent: boolean): void {
        this.origin.scrollX = this.pageContainer.scrollLeft;
        this.origin.scrollY = this.pageContainer.scrollTop;
        this.origin.x = x;
        this.origin.y = y;
        this.origin.clientY = this.getClientY(evt, isTouchEvent);
        this.origin.scale = this.wboDrawerTools.getScale();
    }

    private getClientY(evt, isTouchEvent: boolean): number {
        return isTouchEvent ? evt.changedTouches[0].clientY : evt.clientY;
    }

    private animate(scale: number): number {
        cancelAnimationFrame(this.animation);
        this.animation = requestAnimationFrame(() => this.zoom(this.origin, scale));
        return this.animation;
    }

    private zoom(origin, scale: number): void {
        const oldScale = origin.scale;
        const newScale = this.wboDrawerTools.setScale(scale);
        this.resizeContainer(scale);
        const scrollX = origin.scrollX + origin.x * (newScale - oldScale);
        const scrollY = origin.scrollY + origin.y * (newScale - oldScale);
        this.pageContainer.scrollTo(scrollX, scrollY);
    }

    private resizeContainer(scale: number) {
        this.board.style.width = `${this.svg.width.baseVal.value * scale}px`;
        this.board.style.height = `${this.svg.height.baseVal.value * scale}px`;
    }
}
